import React, { useEffect, useState, useRef } from 'react';
import { GraduationCapIcon, MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { areaStudyOptions, programOptions, degreeLevelOptions } from '../data/filterOptions';
interface HeaderProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onNavigateToProgram?: (areaId: number, programId: number) => void;
  onNavigateToDegree?: (degreeLevel: number) => void;
}
export function Header({
  currentPage,
  onNavigate,
  onNavigateToProgram,
  onNavigateToDegree
}: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);
  const [showDegreeDropdown, setShowDegreeDropdown] = useState(false);
  const [selectedArea, setSelectedArea] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const degreeDropdownRef = useRef<HTMLDivElement>(null);
  const handleNav = (page: string) => {
    onNavigate(page);
    setMenuOpen(false);
  };
  const handleProgramClick = (areaId: number, programId: number) => {
    if (onNavigateToProgram) {
      onNavigate('compare');
      onNavigateToProgram(areaId, programId);
      setShowAreaDropdown(false);
      setSelectedArea(null);
    }
  };
  const handleDegreeClick = (degreeLevel: number) => {
    if (onNavigateToDegree) {
      onNavigateToDegree(degreeLevel);
      setShowDegreeDropdown(false);
      setMenuOpen(false);
    }
  };
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowAreaDropdown(false);
        setSelectedArea(null);
      }
      if (degreeDropdownRef.current && !degreeDropdownRef.current.contains(event.target as Node)) {
        setShowDegreeDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <header className="w-full bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-439" data-mp="1wcxo8v6gwcyx">
      {/* Top Disclosure Banner - hidden on mobile */}
      <div className="hidden md:block w-full bg-gray-50 py-1.5 px-4 text-center border-b border-gray-200 relative" data-id="element-440" data-mp="1hni07mybmlmx">
        <p className="text-xs text-gray-500" data-id="element-441" data-mp="1mki5vwr9dmdi">
          We earn commissions from brands listed on this site, which influences
          how listings are presented.
          <button onMouseEnter={() => setShowDisclosure(true)} onMouseLeave={() => setShowDisclosure(false)} className="underline ml-1 hover:text-gray-700 relative" data-id="element-442" data-mp="1cpggczt2miae">
            Advertising Disclosure
          </button>
        </p>

        {/* Disclosure Popup */}
        {showDisclosure && <div onMouseEnter={() => setShowDisclosure(true)} onMouseLeave={() => setShowDisclosure(false)} className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-full max-w-2xl bg-white border border-gray-200 rounded-lg shadow-xl p-6 z-50 text-left" data-id="element-443" data-mp="2t3yjxsgwrofl">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-444" data-mp="hflbeerqyrs8">
              Advertising Disclosure
            </h3>
            <div className="text-xs text-gray-700 leading-relaxed space-y-2" data-id="element-445" data-mp="w4zljp35dd8o">
              <p data-id="element-446" data-mp="rdrciu5qbtw1">
                www.compareonlinecolleges.com is an online resource that
                strives to offer helpful content and comparison features to our
                visitors. We accept advertising compensation from companies that
                appear on the site, which impacts the location and order in
                which brands (and/or their products) are presented, and also
                impacts the score that is assigned to it.
              </p>
              <p data-id="element-447" data-mp="1a261lbwhkxu7">
                Company listings on this page DO NOT imply endorsement. We do
                not feature all providers on the market. Except as expressly set
                forth in our Terms of Use, all representations and warranties
                regarding the information presented on this page are disclaimed.
              </p>
              <p data-id="element-448" data-mp="27wyufuk2j1vg">
                The information, including pricing, which appears on this site
                is subject to change at any time.
              </p>
            </div>
          </div>}
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between" data-id="element-449" data-mp="2gxnbyz8o7kd3">
        {/* Mobile: Hamburger */}
        <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-1.5 -ml-1.5 text-gray-600 hover:text-gray-900 transition-colors" aria-label="Toggle menu" data-id="element-450" data-mp="1p70r1rr7twy5">
          {menuOpen ? <XIcon className="w-5 h-5" data-id="element-451" data-mp="1t712d3j0ps0z" /> : <MenuIcon className="w-5 h-5" data-id="element-452" data-mp="1y042juiavzq5" />}
        </button>

        {/* Logo - centered on mobile */}
        <button onClick={() => handleNav('compare')} className="flex items-center gap-2 hover:opacity-80 transition-opacity md:mr-auto" data-id="element-453" data-mp="11i7o3bc6eou1">
          <div className="bg-[#e8594f] text-white p-1.5 rounded-lg" data-id="element-454" data-mp="15hq884w6avvt">
            <GraduationCapIcon className="w-5 h-5" data-id="element-455" data-mp="y00fw4h5dc9l" />
          </div>
          <span className="text-lg font-bold text-gray-900 tracking-tight" data-id="element-456" data-mp="t0nttly3o982">
            FindOnlineDegrees
          </span>
        </button>

        {/* Mobile: spacer to balance hamburger for centering */}
        <div className="w-8 md:hidden" data-id="element-457" data-mp="k097qkdbaq3i"></div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" data-id="element-458" data-mp="k2bxd3ajyh7p">
          {/* Area of Study Dropdown */}
          <div className="relative" ref={dropdownRef} data-id="element-459" data-mp="f2qz6v37icw1">
            <button onClick={() => setShowAreaDropdown(!showAreaDropdown)} className="px-3 py-1.5 text-sm font-medium rounded-md transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center gap-1" data-id="element-460" data-mp="a9ea6jum842r">
              Area of Study
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAreaDropdown ? 'rotate-180' : ''}`} data-id="element-461" data-mp="3sfsxh9erxqdt" />
            </button>

            {showAreaDropdown && <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-y-auto z-50" data-id="element-462" data-mp="3nh7ytef67erh">
                <div className="p-2" data-id="element-463" data-mp="3irx6elkyce0q">
                  {areaStudyOptions.slice(0, 8).map(area => {
                const isOpen = selectedArea === area.value;
                const programs = programOptions[area.value] || [];
                return <div key={area.value} className="mb-1" data-id="element-464" data-mp="38usf67c91sd2">
                        <button onClick={() => setSelectedArea(isOpen ? null : area.value)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 rounded-md transition-colors text-left" data-id="element-465" data-mp="eh73967aquf">
                          <span data-id="element-466" data-mp="3mt8wes7qn19g">{area.label}</span>
                          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-467" data-mp="3rslkfgaciedm" />
                        </button>

                        {isOpen && programs.length > 0 && <div className="ml-3 mt-1 space-y-1" data-id="element-468" data-mp="o3i84bcpezpo">
                            {programs.slice(0, 8).map(program => <button key={program.value} onClick={() => handleProgramClick(area.value, program.value)} className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-[#3b82f6] hover:bg-blue-50 rounded-md transition-colors" data-id="element-469" data-mp="28txegkdjlptz">
                                {program.label}
                              </button>)}
                          </div>}
                      </div>;
              })}
                </div>
              </div>}
          </div>

          {/* Degree Options Dropdown */}
          <div className="relative" ref={degreeDropdownRef} data-id="element-470" data-mp="1kx1f2m108i08">
            <button onClick={() => setShowDegreeDropdown(!showDegreeDropdown)} className="px-3 py-1.5 text-sm font-medium rounded-md transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center gap-1" data-id="element-471" data-mp="1kyky2ke1p75p">
              Degree Options
              <ChevronDownIcon className={`w-4 h-4 transition-transform ${showDegreeDropdown ? 'rotate-180' : ''}`} data-id="element-472" data-mp="3a9v4l3erjxrj" />
            </button>

            {showDegreeDropdown && <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50" data-id="element-473" data-mp="13n5x3ij78t1z">
                <div className="p-2" data-id="element-474" data-mp="18n1zjdc90cff">
                  {degreeLevelOptions.filter(d => d.value !== 5).map(degree => <button key={degree.value} onClick={() => handleDegreeClick(degree.value)} className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 rounded-md transition-colors" data-id="element-475" data-mp="1dk26dqgx9j7l">
                        {degree.label}
                      </button>)}
                </div>
              </div>}
          </div>
        </nav>
      </div>

      {/* Mobile slide-down menu */}
      {menuOpen && <div className="md:hidden border-t border-gray-100 bg-white shadow-lg" data-id="element-476" data-mp="1wc4f8fnbnv7a">
          <nav className="px-4 py-2" data-id="element-477" data-mp="219mok7594cft">
            <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-478" data-mp="218lcq0gevlx1">
              Degree Options
            </p>
            {degreeLevelOptions.filter(d => d.value !== 5).map(degree => <button key={degree.value} onClick={() => handleDegreeClick(degree.value)} className="w-full text-left px-3 py-3 text-sm font-medium rounded-lg transition-colors text-gray-700 hover:bg-gray-50" data-id="element-479" data-mp="6yhqrjguiu8e">
                  {degree.label}
                </button>)}
          </nav>
        </div>}
    </header>;
}